// PPA (Power Purchase Agreement) settlement config.
// Composable schema: the real-world PPA "types" are expressed as combinations of
// orthogonal dimensions (structure, volume basis, price shape, protection,
// indexation, tenor, treatment), prefilled by named presets. Mirrors the backend
// BESS_feasibility/Configuration/ppa.py so buildPpaPayload() emits exactly the
// keys normalize_ppa_config() expects.
//
// Bankability: only the CONTRACTED portion enters base-case NPV/IRR/DSCR. Merchant
// residual, above-floor upside and the post-PPA tail are DISCLOSURE_ONLY by
// default; the user may flip a component to CONTRACTED_BASE_CASE (disclosed).

// ---- grounded defaults (see PYTHIA_PPA_modeling_reference.md §9) ------------
const DEFAULT_CAPTURE_RATE = 0.6; // core-EU solar capture factor, 2025 (falling)
const DEFAULT_IMBALANCE_COST = 3.0; // EUR/MWh balancing/BRP cost, 2023
const DEFAULT_CONTRACT_TERM_YEARS = 10; // 10-yr PaP benchmark

export const DEFAULT_PPA_CONFIG = {
  // A. Settlement structure
  settlementStructure: "physical",
  cfdDirection: "two_way",
  cfdVolumeDefinition: "as_produced",
  referenceIndex: "zonal_day_ahead",
  basisAdjustmentPerMwh: 0,
  // B. Volume basis
  volumeBasis: "pay_as_produced",
  contractedSharePct: 100,
  baseloadMode: "flat",
  baseloadMw: 0,
  targetProfileMode: "flat_24x7",
  matchingTolerancePct: 0,
  unmatchedTreatment: "merchant",
  penaltyPricePerMwh: 0,
  cfeAdditionality: true,
  firmBlockLimitKw: 0,
  firmBlockStartHour: "00:00",
  firmBlockEndHour: "23:59",
  // C. Price shape
  priceShape: "flat",
  basePrice: 0,
  priceUnit: "eur_per_mwh",
  todBlocks: [],
  // C. Indexation
  escalationMode: "none",
  annualEscalationPct: 0,
  cpiParticipationPct: 100,
  realOrNominal: "nominal",
  // D. Price protection
  priceProtection: "none",
  floorPricePerMwh: 0,
  capPricePerMwh: 0,
  projectUpsideSharePct: 0,
  // E. Tenor + tail
  contractTermYears: DEFAULT_CONTRACT_TERM_YEARS,
  postContractRevenueMode: "merchant_reversion",
  // F. Revenue treatment (strict default + per-component override)
  merchantResidualTreatment: "DISCLOSURE_ONLY",
  postContractTailTreatment: "DISCLOSURE_ONLY",
  aboveFloorUpsideTreatment: "DISCLOSURE_ONLY",
  // Merchant valuation
  captureRateMode: "flat",
  captureRate: DEFAULT_CAPTURE_RATE,
  captureRateDeclinePct: 0,
  negativePriceRule: "curtail_zero",
  negativePriceTransferPct: 0,
  merchantHaircutPct: 0,
  merchantDiscountRate: "",
  // Shape / imbalance
  imbalanceCostPerMwh: DEFAULT_IMBALANCE_COST,
  // Debt / bankability
  yieldProbabilityForDebt: "P90",
  sizeDebtToContractedOnly: true,
  showFairPrice: true,
  // Applicability
  appliesTo: "pv_plus_bess",
};

export const PPA_SETTLEMENT_STRUCTURE_OPTIONS = [
  { value: "physical", label: "Physical (delivered energy)" },
  { value: "financial_cfd", label: "Financial / virtual (CfD)" },
];

export const PPA_CFD_DIRECTION_OPTIONS = [
  { value: "two_way", label: "Two-way swap" },
  { value: "one_way_floor", label: "One-way (floor / put)" },
];

export const PPA_VOLUME_BASIS_OPTIONS = [
  { value: "pay_as_produced", label: "Pay-as-produced (as generated)" },
  { value: "baseload_block", label: "Baseload / shaped block" },
  { value: "hourly_matched_24x7", label: "24x7 hourly-matched (CFE)" },
  { value: "percent_of_generation", label: "Percent of generation" },
  { value: "firm_block", label: "Firm block (time window)" },
];

export const PPA_PRICE_SHAPE_OPTIONS = [
  { value: "flat", label: "Flat price" },
  { value: "tod_blocks", label: "Time-of-day blocks" },
  { value: "indexed", label: "Indexed / escalated" },
];

export const PPA_PRICE_UNIT_OPTIONS = [
  { value: "eur_per_mwh", label: "EUR/MWh" },
  { value: "eur_per_kwh", label: "EUR/kWh" },
];

export const PPA_PRICE_PROTECTION_OPTIONS = [
  { value: "none", label: "None" },
  { value: "floor", label: "Floor (put)" },
  { value: "collar", label: "Collar (floor + cap)" },
  { value: "floor_plus_upside_share", label: "Floor + upside share" },
];

export const PPA_ESCALATION_MODE_OPTIONS = [
  { value: "none", label: "None (flat nominal)" },
  { value: "fixed_pct", label: "Fixed % per year" },
  { value: "cpi_full", label: "CPI-linked (full)" },
  { value: "cpi_partial", label: "CPI-linked (partial)" },
  { value: "custom_curve", label: "Custom curve" },
];

export const PPA_NEGATIVE_PRICE_RULE_OPTIONS = [
  { value: "curtail_zero", label: "Curtail (zero revenue)" },
  { value: "pay_as_bid", label: "Pay as bid (deliver anyway)" },
  { value: "suspend_settlement", label: "Suspend PPA settlement" },
];

export const PPA_CAPTURE_RATE_MODE_OPTIONS = [
  { value: "flat", label: "Flat capture rate" },
  { value: "annual_decline_pct", label: "Annual decline (pp/yr)" },
  { value: "custom_curve", label: "Custom curve" },
];

export const PPA_POST_CONTRACT_MODE_OPTIONS = [
  { value: "merchant_reversion", label: "Merchant reversion" },
  { value: "zero", label: "Zero" },
  { value: "continue_fixed", label: "Continue PPA price" },
];

export const PPA_TREATMENT_OPTIONS = [
  { value: "DISCLOSURE_ONLY", label: "Disclosure only / upside case" },
  { value: "CONTRACTED_BASE_CASE", label: "Contracted — included in base case" },
];

export const PPA_APPLIES_TO_OPTIONS = [
  { value: "pv_generation", label: "PV generation only" },
  { value: "pv_plus_bess", label: "PV + BESS (combined POI)" },
  { value: "bess_standalone", label: "BESS standalone" },
];

export const PPA_YIELD_PROBABILITY_OPTIONS = [
  { value: "P50", label: "P50 (median)" },
  { value: "P90", label: "P90 (1-year exceedance)" },
  { value: "P99", label: "P99 (conservative)" },
];

export const PPA_UNMATCHED_TREATMENT_OPTIONS = [
  { value: "merchant", label: "Merchant" },
  { value: "penalty", label: "Penalty price" },
];

// Presets prefill the composable schema. Each is a set of field overrides.
export const PPA_PRESETS = {
  physical_pay_as_produced: { settlementStructure: "physical", volumeBasis: "pay_as_produced", priceShape: "flat", priceProtection: "none" },
  physical_tod: { settlementStructure: "physical", volumeBasis: "pay_as_produced", priceShape: "tod_blocks", priceProtection: "none" },
  baseload: { settlementStructure: "physical", volumeBasis: "baseload_block", priceShape: "flat", priceProtection: "none" },
  hourly_matched_24x7: { settlementStructure: "physical", volumeBasis: "hourly_matched_24x7", priceShape: "flat", priceProtection: "none", cfeAdditionality: true },
  virtual_cfd: { settlementStructure: "financial_cfd", cfdDirection: "two_way", cfdVolumeDefinition: "as_produced", volumeBasis: "pay_as_produced", priceShape: "flat", priceProtection: "none" },
  floor_collar: { settlementStructure: "financial_cfd", cfdDirection: "one_way_floor", volumeBasis: "pay_as_produced", priceShape: "flat", priceProtection: "collar" },
  floor_plus_upside: { settlementStructure: "physical", volumeBasis: "pay_as_produced", priceShape: "flat", priceProtection: "floor_plus_upside_share" },
  merchant_ppa_hybrid: { settlementStructure: "physical", volumeBasis: "percent_of_generation", priceShape: "flat", priceProtection: "none", contractedSharePct: 70 },
  firm_block_legacy: { settlementStructure: "physical", volumeBasis: "firm_block", priceShape: "flat", priceProtection: "none" },
};

export const PPA_PRESET_OPTIONS = [
  { value: "physical_pay_as_produced", label: "Physical, pay-as-produced (fixed)" },
  { value: "physical_tod", label: "Physical, time-of-day shaped" },
  { value: "baseload", label: "Baseload / shaped" },
  { value: "hourly_matched_24x7", label: "24x7 hourly-matched (CFE)" },
  { value: "virtual_cfd", label: "Virtual / financial CfD" },
  { value: "floor_collar", label: "Floor / collar" },
  { value: "floor_plus_upside", label: "Floor + upside share" },
  { value: "merchant_ppa_hybrid", label: "Merchant + PPA hybrid" },
  { value: "firm_block_legacy", label: "Firm block (legacy)" },
];

export const PPA_HELPER_TEXT =
  "Composable PPA: pick a preset or set the dimensions directly. Only the contracted portion enters base-case NPV/IRR/DSCR; merchant residual, above-floor upside and the post-PPA tail are disclosure-only unless you override a component. The PPA prices generation (exported energy), not on-site load. Capture rate, escalation, floor/collar, tenor and treatments are all inputs — grounded defaults are conservative (see modeling reference).";

const OPTION_SETS = {
  settlementStructure: PPA_SETTLEMENT_STRUCTURE_OPTIONS,
  cfdDirection: PPA_CFD_DIRECTION_OPTIONS,
  cfdVolumeDefinition: [{ value: "as_produced" }, { value: "fixed_shape" }],
  volumeBasis: PPA_VOLUME_BASIS_OPTIONS,
  priceShape: PPA_PRICE_SHAPE_OPTIONS,
  priceUnit: PPA_PRICE_UNIT_OPTIONS,
  priceProtection: PPA_PRICE_PROTECTION_OPTIONS,
  escalationMode: PPA_ESCALATION_MODE_OPTIONS,
  negativePriceRule: PPA_NEGATIVE_PRICE_RULE_OPTIONS,
  captureRateMode: PPA_CAPTURE_RATE_MODE_OPTIONS,
  postContractRevenueMode: PPA_POST_CONTRACT_MODE_OPTIONS,
  merchantResidualTreatment: PPA_TREATMENT_OPTIONS,
  postContractTailTreatment: PPA_TREATMENT_OPTIONS,
  aboveFloorUpsideTreatment: PPA_TREATMENT_OPTIONS,
  appliesTo: PPA_APPLIES_TO_OPTIONS,
  yieldProbabilityForDebt: PPA_YIELD_PROBABILITY_OPTIONS,
  unmatchedTreatment: PPA_UNMATCHED_TREATMENT_OPTIONS,
  baseloadMode: [{ value: "flat" }, { value: "custom_curve" }],
  targetProfileMode: [{ value: "flat_24x7" }, { value: "custom_curve" }],
};

function validSet(key) {
  return new Set((OPTION_SETS[key] || []).map((o) => o.value));
}

function parseFiniteNumber(value, fallback = 0) {
  if (value === "" || value == null) return fallback;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function pick(source, camel, snake) {
  const v = source?.[camel];
  if (v !== undefined && v !== null) return v;
  return source?.[snake];
}

function enumOr(source, camel, snake, key, fallback) {
  const raw = pick(source, camel, snake);
  return validSet(key).has(raw) ? raw : fallback;
}

function clampPct(value, fallback = 0) {
  return Math.max(0, Math.min(100, parseFiniteNumber(value, fallback)));
}

function normalizeTodBlocks(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((b) => b && typeof b === "object")
    .map((b, i) => ({
      label: String(b.label ?? b.name ?? `Block ${i + 1}`),
      startHour: String(b.startHour ?? b.start ?? "00:00"),
      endHour: String(b.endHour ?? b.end ?? "23:59"),
      price: parseFiniteNumber(b.price ?? b.pricePerMwh, 0),
    }));
}

export function applyPpaPreset(presetName, overrides = {}) {
  return {
    ...DEFAULT_PPA_CONFIG,
    ...(PPA_PRESETS[presetName] || {}),
    ...overrides,
  };
}

export function normalizePpaConfig(source = {}) {
  const d = DEFAULT_PPA_CONFIG;
  return {
    settlementStructure: enumOr(source, "settlementStructure", "settlement_structure", "settlementStructure", d.settlementStructure),
    cfdDirection: enumOr(source, "cfdDirection", "cfd_direction", "cfdDirection", d.cfdDirection),
    cfdVolumeDefinition: enumOr(source, "cfdVolumeDefinition", "cfd_volume_definition", "cfdVolumeDefinition", d.cfdVolumeDefinition),
    referenceIndex: String(pick(source, "referenceIndex", "reference_index") ?? d.referenceIndex),
    basisAdjustmentPerMwh: parseFiniteNumber(pick(source, "basisAdjustmentPerMwh", "basis_adjustment_per_mwh"), d.basisAdjustmentPerMwh),

    volumeBasis: enumOr(source, "volumeBasis", "volume_basis", "volumeBasis", d.volumeBasis),
    contractedSharePct: clampPct(pick(source, "contractedSharePct", "contracted_share_pct"), d.contractedSharePct),
    baseloadMode: enumOr(source, "baseloadMode", "baseload_mode", "baseloadMode", d.baseloadMode),
    baseloadMw: Math.max(0, parseFiniteNumber(pick(source, "baseloadMw", "baseload_mw"), d.baseloadMw)),
    targetProfileMode: enumOr(source, "targetProfileMode", "target_profile_mode", "targetProfileMode", d.targetProfileMode),
    matchingTolerancePct: clampPct(pick(source, "matchingTolerancePct", "matching_tolerance_pct"), d.matchingTolerancePct),
    unmatchedTreatment: enumOr(source, "unmatchedTreatment", "unmatched_treatment", "unmatchedTreatment", d.unmatchedTreatment),
    penaltyPricePerMwh: Math.max(0, parseFiniteNumber(pick(source, "penaltyPricePerMwh", "penalty_price_per_mwh"), d.penaltyPricePerMwh)),
    cfeAdditionality: pick(source, "cfeAdditionality", "cfe_additionality") ?? d.cfeAdditionality,
    firmBlockLimitKw: Math.max(0, parseFiniteNumber(pick(source, "firmBlockLimitKw", "firm_block_limit_kw"), d.firmBlockLimitKw)),
    firmBlockStartHour: String(pick(source, "firmBlockStartHour", "firm_block_start_hour") ?? d.firmBlockStartHour),
    firmBlockEndHour: String(pick(source, "firmBlockEndHour", "firm_block_end_hour") ?? d.firmBlockEndHour),

    priceShape: enumOr(source, "priceShape", "price_shape", "priceShape", d.priceShape),
    basePrice: Math.max(0, parseFiniteNumber(pick(source, "basePrice", "base_price"), d.basePrice)),
    priceUnit: enumOr(source, "priceUnit", "price_unit", "priceUnit", d.priceUnit),
    todBlocks: normalizeTodBlocks(pick(source, "todBlocks", "tod_blocks")),

    escalationMode: enumOr(source, "escalationMode", "escalation_mode", "escalationMode", d.escalationMode),
    annualEscalationPct: parseFiniteNumber(pick(source, "annualEscalationPct", "annual_escalation_pct"), d.annualEscalationPct),
    cpiParticipationPct: clampPct(pick(source, "cpiParticipationPct", "cpi_participation_pct"), d.cpiParticipationPct),
    realOrNominal: pick(source, "realOrNominal", "real_or_nominal") === "real" ? "real" : "nominal",

    priceProtection: enumOr(source, "priceProtection", "price_protection", "priceProtection", d.priceProtection),
    floorPricePerMwh: Math.max(0, parseFiniteNumber(pick(source, "floorPricePerMwh", "floor_price_per_mwh"), d.floorPricePerMwh)),
    capPricePerMwh: Math.max(0, parseFiniteNumber(pick(source, "capPricePerMwh", "cap_price_per_mwh"), d.capPricePerMwh)),
    projectUpsideSharePct: clampPct(pick(source, "projectUpsideSharePct", "project_upside_share_pct"), d.projectUpsideSharePct),

    contractTermYears: Math.max(0, Math.floor(parseFiniteNumber(pick(source, "contractTermYears", "contract_term_years"), d.contractTermYears))),
    postContractRevenueMode: enumOr(source, "postContractRevenueMode", "post_contract_revenue_mode", "postContractRevenueMode", d.postContractRevenueMode),

    merchantResidualTreatment: enumOr(source, "merchantResidualTreatment", "merchant_residual_treatment", "merchantResidualTreatment", d.merchantResidualTreatment),
    postContractTailTreatment: enumOr(source, "postContractTailTreatment", "post_contract_tail_treatment", "postContractTailTreatment", d.postContractTailTreatment),
    aboveFloorUpsideTreatment: enumOr(source, "aboveFloorUpsideTreatment", "above_floor_upside_treatment", "aboveFloorUpsideTreatment", d.aboveFloorUpsideTreatment),

    captureRateMode: enumOr(source, "captureRateMode", "capture_rate_mode", "captureRateMode", d.captureRateMode),
    captureRate: Math.max(0, Math.min(1.5, parseFiniteNumber(pick(source, "captureRate", "capture_rate"), d.captureRate))),
    captureRateDeclinePct: parseFiniteNumber(pick(source, "captureRateDeclinePct", "capture_rate_decline_pct"), d.captureRateDeclinePct),
    negativePriceRule: enumOr(source, "negativePriceRule", "negative_price_rule", "negativePriceRule", d.negativePriceRule),
    negativePriceTransferPct: parseFiniteNumber(pick(source, "negativePriceTransferPct", "negative_price_transfer_pct"), d.negativePriceTransferPct),
    merchantHaircutPct: clampPct(pick(source, "merchantHaircutPct", "merchant_haircut_pct"), d.merchantHaircutPct),
    merchantDiscountRate: pick(source, "merchantDiscountRate", "merchant_discount_rate") ?? "",

    imbalanceCostPerMwh: Math.max(0, parseFiniteNumber(pick(source, "imbalanceCostPerMwh", "imbalance_cost_per_mwh"), d.imbalanceCostPerMwh)),

    yieldProbabilityForDebt: enumOr(source, "yieldProbabilityForDebt", "yield_probability_for_debt", "yieldProbabilityForDebt", d.yieldProbabilityForDebt),
    sizeDebtToContractedOnly: pick(source, "sizeDebtToContractedOnly", "size_debt_to_contracted_only") ?? d.sizeDebtToContractedOnly,
    showFairPrice: pick(source, "showFairPrice", "show_fair_price") ?? d.showFairPrice,

    appliesTo: enumOr(source, "appliesTo", "applies_to", "appliesTo", d.appliesTo),
  };
}

export function buildPpaPayload(source = {}) {
  const n = normalizePpaConfig(source);
  const merchantDiscountRate =
    n.merchantDiscountRate === "" || n.merchantDiscountRate == null
      ? null
      : parseFiniteNumber(n.merchantDiscountRate, null);
  return { ...n, enabled: true, merchantDiscountRate };
}

export function validatePpaConfig({ enabled = true, ppaConfig = {} } = {}) {
  if (!enabled) return { errors: [], warnings: [] };
  const n = normalizePpaConfig(ppaConfig);
  const errors = [];
  const warnings = [];

  const contractsSomething =
    n.basePrice > 0 || n.floorPricePerMwh > 0 || (n.todBlocks && n.todBlocks.length > 0);
  if (!contractsSomething) {
    errors.push("PPA has no contracted price (base price, floor, or ToD blocks are all zero/empty).");
  }
  if (["floor", "collar", "floor_plus_upside_share"].includes(n.priceProtection) && n.floorPricePerMwh <= 0) {
    warnings.push("Price protection selected but the floor price is zero.");
  }
  if (n.settlementStructure === "financial_cfd" && n.basePrice <= 0) {
    warnings.push("Financial CfD selected but the strike (base price) is zero.");
  }
  if (n.volumeBasis === "hourly_matched_24x7" && n.appliesTo === "pv_generation") {
    warnings.push("24x7 hourly matching requires the battery; set 'Applies to' to PV + BESS.");
  }
  const merchantExposed =
    ["baseload_block", "hourly_matched_24x7", "percent_of_generation", "firm_block"].includes(n.volumeBasis) ||
    ["floor", "collar", "floor_plus_upside_share"].includes(n.priceProtection) ||
    n.contractTermYears > 0;
  if (merchantExposed && n.captureRateMode === "flat" && n.captureRateDeclinePct === 0) {
    warnings.push("Merchant-exposed revenue uses a flat capture rate; solar capture rates decline over time — consider a declining curve.");
  }
  return { errors, warnings };
}
